import {
  selectBugReportScreenshots,
  type RejectedScreenshot,
  type ScreenshotSelection,
} from '@pypestream/utils';

/**
 * The decisions the Report a bug form makes, kept out of the component.
 *
 * The component imports design-system Lit elements, which do not survive this
 * package's test environment (`app-error-boundary.test.tsx` stubs them for
 * exactly that reason) — so the rules live here, where a test reaches them
 * directly, and the component is left with the wiring.
 */

const PASTED_SCREENSHOT_PREFIX = 'pasted-screenshot';

/**
 * What a pasted image is called on disk, by MIME type.
 *
 * Only what a clipboard actually hands over for a screenshot: macOS and Windows
 * both put PNG on it, and an image copied out of a page can arrive as JPEG,
 * GIF or WebP.
 */
const EXTENSION_BY_TYPE: Record<string, string> = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/gif': 'gif',
  'image/webp': 'webp',
};

const extensionFor = (type: string): string =>
  EXTENSION_BY_TYPE[type] ?? type.split('/')[1] ?? 'png';

/**
 * Reads the screenshots out of a paste.
 *
 * Cmd+Shift+4 then Cmd+V is how a screenshot is normally taken, and the
 * clipboard hands that over as a file on the paste event — so the form listens
 * for the paste rather than asking the user to save the image first.
 *
 * Every one of them arrives named `image.png`, whatever it shows, so the name
 * is replaced by a number: three pastes would otherwise reach Sentry as three
 * attachments with one name.
 *
 * `alreadyPasted` counts pastes made SO FAR, not files currently attached, and
 * the caller must not conflate the two. Counting what is attached repeats a
 * name as soon as one is removed — paste twice, drop the first, paste again,
 * and the third is `pasted-screenshot-2` all over again, which collides in the
 * Sentry attachment list and duplicates a React key in the previews.
 *
 * `navigator.clipboard.read()` is deliberately NOT used. It needs the
 * `clipboard-read` permission, prompts on first use, and is unimplemented in
 * Firefox — while the paste event needs no permission at all and is the gesture
 * the user is already making.
 */
export const extractPastedScreenshots = (
  clipboardData: DataTransfer | null,
  alreadyPasted: number
): File[] => {
  if (!clipboardData) {
    return [];
  }

  return Array.from(clipboardData.files)
    .filter((file) => file.type.startsWith('image/'))
    .map(
      (file, index) =>
        new File(
          [file],
          `${PASTED_SCREENSHOT_PREFIX}-${alreadyPasted + index + 1}.${extensionFor(
            file.type
          )}`,
          { type: file.type }
        )
    );
};

/**
 * Adds newly picked or pasted files to what the report already carries.
 *
 * The budget is per REPORT, so the new files are judged against what the old
 * ones already spent. The already-attached files are passed through the same
 * selection FIRST, which is what guarantees they survive it: they cleared these
 * very checks when they were added, in this order.
 */
export const mergeBugReportScreenshots = (
  existing: File[],
  picked: File[]
): ScreenshotSelection => selectBugReportScreenshots([...existing, ...picked]);

export type { RejectedScreenshot };

/**
 * Whether there is anything to send.
 *
 * A screenshot alone is a report — a picture of the broken screen says more
 * than most sentences — so the form does not insist on words.
 */
export const canSubmitBugReport = ({
  message,
  screenshots,
}: {
  message: string;
  screenshots: File[];
}): boolean => Boolean(message.trim()) || screenshots.length > 0;
